import { useState } from 'react';
import type { MultiselectProps } from '../types';
import { Multiselect } from './Multiselect';

type RequiredMultiselectProps<TValue> = Omit<
  MultiselectProps<TValue>,
  'indicator' | 'errorMessage' | 'isRequired' | 'hasError' | 'defaultValue'
> &
  Required<Pick<MultiselectProps<TValue>, 'errorMessage'>> & {
    /**
     * Some forms validate inputs on hitting the submit button, whereas other disable the button until all fields are valid.
     * For the former one, pass 'shouldShowError' prop to show error without user touching the input.
     */
    shouldShowError?: boolean;
  };

/**
 * Multiselect counterpart of `RequiredTextInput`.
 *
 * Pass errorMessage that should be displayed when user blurs the select without selecting any option.
 * Shows error only after focusing out.
 *
 * This component is stateful - it keeps the information if it has been touched.
 */
export const RequiredMultiselect = <TValue,>(
  props: RequiredMultiselectProps<TValue>
) => {
  const [touched, setTouched] = useState(false);
  const { shouldShowError, errorMessage, ...rest } = props;
  const valueEmpty = !rest.value || rest.value.length === 0;
  const hasError = valueEmpty && (touched || Boolean(shouldShowError));

  return (
    <Multiselect
      {...rest}
      indicator="required"
      hasError={hasError}
      errorMessage={hasError ? errorMessage : ''}
      onBlur={event => {
        setTouched(true);
        rest.onBlur?.(event);
      }}
    />
  );
};
